import React, { useEffect, useMemo, useRef, useState } from "react";
import { EmptyStateErro } from "../../components/EmptyState";
import NavegacaoEtapa from "../../components/NavegacaoEtapa";
import FilterPanel from "../../components/FilterPanel";
import BotaoExportacao from "../../components/Exportacao";
import { PageLoader } from "../../components/LoadingMarcenaria";
import { useExportSnapshot } from "../../hooks/useExportSnapshot";
import { SoIcone } from "../../components/ListaCompacta/styles";
import {
  BuscaInput,
  FilterField,
  FilterGrid,
  FilterInlineGroup,
  FilterLabel,
  FiltroSelect,
} from "../../components/PainelTabela/styles";
import { useDados } from "../../context/DadosContext";
import { usePrecosVisiveis } from "../../context/PrecosContext";
import { formatarMoeda } from "../../utils/calculos";
import { SCALES } from "../../components/Calendar";
import { getColunasMaquinas } from "../../utils/industrializacao";
import {
  INITIAL_FILTERS,
  LISTA_OPERACOES,
  agruparLotesPorOperacao,
  buildFiltrosAtivos,
  buildLotePorId,
  buildLotes,
  buildResumo,
  filtrarLotes,
  getUniqueOptions,
} from "./dominio";
import CalendarioView from "./CalendarioView";
import HistoricoView, { SeletorPeriodo } from "./HistoricoView";
import KanbanView from "./KanbanView";
import LoteDrawer from "./LoteDrawer";
import TabelaView, { MODOS_TABELA, SeletorModoTabela } from "./TabelaView";
import { PERIODOS_HISTORICO, buildLinhasAmbiente } from "./visoes";
import {
  FiltroSoIcone,
  SecaoTitulo,
  ToggleCheck,
  VisaoBar,
  VisaoControles,
  VisaoTab,
  VisaoTabs,
  SummaryCard,
  SummaryGrid,
  SummaryLabel,
  SummarySub,
  SummaryValue,
  Titulo,
  TopBar,
  TopBarActions,
  Wrapper,
} from "./styles";

export const VISOES = [
  { id: "kanban", label: "Kanban" },
  { id: "tabela", label: "Tabela" },
  { id: "calendario", label: "Calendário" },
  { id: "historico", label: "Histórico" },
];

// Preferências de exibição (cosméticas, por navegador — não são filtros).
const PREF = {
  visao: "pharuerp:industrializacao:visao",
  tabela: "pharuerp:industrializacao:tabela-modo",
  escala: "pharuerp:industrializacao:calendario-escala",
  realizados: "pharuerp:industrializacao:calendario-realizados",
  periodo: "pharuerp:industrializacao:historico-periodo",
};

function lerPreferencia(chave, validos, padrao) {
  try {
    const valor = window.localStorage.getItem(chave);
    return validos.includes(valor) ? valor : padrao;
  } catch {
    return padrao;
  }
}

function usePreferencia(chave, validos, padrao) {
  const [valor, setValor] = useState(() => lerPreferencia(chave, validos, padrao));
  useEffect(() => {
    try {
      window.localStorage.setItem(chave, valor);
    } catch {
      // Navegação privada/armazenamento bloqueado: a preferência só não persiste.
    }
  }, [chave, valor]);
  return [valor, setValor];
}

const COLUNAS_AMBIENTE_EXPORT = [
  { key: "loteLabel", label: "Lote", type: "text" },
  { key: "cliente", label: "Cliente", type: "text" },
  { key: "ambiente", label: "Ambiente", type: "text" },
  { key: "mdfBordo", label: "MDF chegou", type: "date" },
  ...getColunasMaquinas().map((op) => ({ key: `data_${op.chave}`, label: op.titulo, type: "date" })),
  { key: "etapa", label: "Etapa", type: "text" },
  { key: "limite", label: "Limite", type: "date" },
  { key: "status", label: "Status do lote", type: "status" },
];

function linhasAmbienteParaExport(lotes) {
  return buildLinhasAmbiente(lotes).map(({ lote, datas, ...linha }) => ({
    ...linha,
    ...Object.fromEntries(getColunasMaquinas().map((op) => [`data_${op.chave}`, datas[op.chave]])),
  }));
}

const SECAO_VISUAL_POR_VISAO = {
  kanban: { id: "lotes", label: "Lotes em máquinas", selector: "[data-pdf='lotes']" },
  tabela: { id: "lotes", label: "Tabela de lotes", selector: "[data-pdf='lotes']" },
  calendario: { id: "lotes", label: "Calendário das máquinas", selector: "[data-pdf='lotes']" },
  historico: { id: "lotes", label: "Histórico das máquinas", selector: "[data-pdf='lotes']" },
};

export default function Industrializacao() {
  const { dados, erro, initialLoading } = useDados();
  const { precosVisiveis } = usePrecosVisiveis();
  // F9 (2026-05-29): guarda o objeto do lote direto no estado (nao
  // derivado de lotes.find). Antes, derivar de `lotes` fazia o
  // loteSelecionado virar nova referencia a cada re-render do DadosContext
  // (mesmo conteudo), perturbando a animacao do SlideOverPanel
  // (transform congelava no meio, painel transbordava). Snapshot no
  // clique eh suficiente para um drawer de detalhes.
  const [loteSelecionado, setLoteSelecionado] = useState(null);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [filters, setFilters] = useState({ ...INITIAL_FILTERS });
  const [visao, setVisao] = usePreferencia(PREF.visao, VISOES.map((v) => v.id), "kanban");
  const [modoTabela, setModoTabela] = usePreferencia(
    PREF.tabela,
    MODOS_TABELA.map((m) => m.id),
    "lote"
  );
  const [escala, setEscala] = usePreferencia(PREF.escala, SCALES, "month");
  const [realizadosPref, setRealizadosPref] = usePreferencia(PREF.realizados, ["1", "0"], "1");
  const [periodo, setPeriodo] = usePreferencia(
    PREF.periodo,
    PERIODOS_HISTORICO.map((p) => p.id),
    "30"
  );
  const realizados = realizadosPref === "1";
  const pageRef = useRef(null);

  const lotes = useMemo(() => buildLotes(dados), [dados]);

  const clienteOptions = useMemo(
    () => ["TODOS", ...getUniqueOptions(lotes.map((lote) => lote.clientePrincipal))],
    [lotes]
  );

  const filteredLotes = useMemo(() => filtrarLotes(lotes, filters), [filters, lotes]);

  const lotesPorOperacao = useMemo(() => agruparLotesPorOperacao(filteredLotes), [filteredLotes]);

  const activeFilterChips = useMemo(() => buildFiltrosAtivos(filters), [filters]);

  const resumo = useMemo(() => buildResumo(filteredLotes), [filteredLotes]);

  // Calendário e histórico abrem lotes que já saíram da fila (concluídos).
  function abrirLotePorId(loteId) {
    setLoteSelecionado(buildLotePorId(dados, loteId));
  }

  // F9 (2026-05-29): removido o listener de ESC manual — o SlideOverPanel
  // ja trata ESC, click-fora e focus trap internamente.

  const exportSnapshotPayload = useMemo(() => {
    if (!resumo) return null;
    const colunasLoteSnap = [
      { key: "loteLabel",        label: "Lote",         type: "text" },
      { key: "clientePrincipal", label: "Cliente",      type: "text" },
      { key: "totalAmbientes",   label: "Ambientes",    type: "number" },
      { key: "totalPendentes",   label: "Pendentes",    type: "number" },
      // "text": o valor é um INTERVALO pré-formatado ("dd/mm/aaaa a
      // dd/mm/aaaa") — como "date", o parser capturava só a 1ª data e "—"
      // virava célula vazia no CSV/XLSX.
      { key: "dataOtimizacao",   label: "Otimização",   type: "text" },
      { key: "dataLimiteMaquinas", label: "Limite",     type: "date" },
      { key: "status",           label: "Status",       type: "status" },
      { key: "valorTotal",       label: "Valor",        type: "currency" },
    ];
    return {
      titulo: "Industrialização (Máquinas)",
      // Gate do agendador — não publica snapshot do 1º render vazio.
      // Gate do agendador. `!initialLoading` sozinho nao distingue "carregou"
      // de "falhou": em todo caminho de erro do DadosContext o estado vira
      // dados=[] + erro + loading=false, entao o snapshot saia "pronto" com
      // ZERO linhas — e o Excel/PDF agendado chegava vazio na pasta, sem aviso.
      // E o cenario mais provavel no iframe, que nao tem gesto para pedir
      // permissao do arquivo da planilha.
      pronto: !initialLoading && !erro && dados.length > 0,
      resumo: [
        { label: "Lotes",              value: filteredLotes.length, type: "number" },
        { label: "Ambientes pendentes", value: resumo.totalAmbientesPendentes, type: "number" },
        { label: "Lotes em atraso",    value: resumo.lotesEmAtraso, type: "number" },
        { label: "Valor total",        value: resumo.valorTotal,    type: "currency" },
      ],
      secoes: getColunasMaquinas().map((operacao) => ({
        id: operacao.chave,
        label: `Aguardando ${operacao.titulo}`,
        columns: colunasLoteSnap,
        rows: lotesPorOperacao[operacao.chave],
      })).concat({
        id: "ambientes",
        label: "Ambientes pendentes",
        columns: COLUNAS_AMBIENTE_EXPORT,
        rows: linhasAmbienteParaExport(filteredLotes),
      }),
      visual: {
        ref: pageRef,
        sections: [
          { id: "resumo", label: "Resumo dos lotes", selector: "[data-pdf='resumo']" },
          SECAO_VISUAL_POR_VISAO[visao],
        ],
      },
    };
  }, [resumo, filteredLotes, lotesPorOperacao, initialLoading, erro, dados.length, visao]);
  useExportSnapshot("industrializacao", exportSnapshotPayload, pageRef);

  if (initialLoading) {
    return <PageLoader scope="industrializacao" />;
  }

  if (erro) {
    return <EmptyStateErro erro={erro} />;
  }

  const colunasLote = [
    { key: "loteLabel",        label: "Lote",         type: "text" },
    { key: "clientePrincipal", label: "Cliente",      type: "text" },
    { key: "totalAmbientes",   label: "Ambientes",    type: "number" },
    { key: "totalPendentes",   label: "Pendentes",    type: "number" },
    // "text": intervalo pré-formatado — ver colunasLoteSnap acima.
    { key: "dataOtimizacao",   label: "Otimização",   type: "text" },
    { key: "dataLimiteMaquinas", label: "Limite",     type: "date" },
    { key: "status",           label: "Status",       type: "status" },
    { key: "valorTotal",       label: "Valor",        type: "currency" },
  ];

  const exportResumo = [
    { label: "Lotes",              value: filteredLotes.length, type: "number" },
    { label: "Ambientes pendentes", value: resumo.totalAmbientesPendentes, type: "number" },
    { label: "Lotes em atraso",    value: resumo.lotesEmAtraso, type: "number" },
    ...getColunasMaquinas().map((operacao) => ({
      label: `Aguardando ${operacao.titulo.toLowerCase()}`,
      value: resumo.aguardandoPorOperacao[operacao.chave],
      type: "number",
    })),
    { label: "Valor total",        value: resumo.valorTotal,    type: "currency" },
  ];

  const exportSecoes = getColunasMaquinas()
    .map((operacao) => ({
      id: operacao.chave,
      label: `Aguardando ${operacao.titulo}`,
      columns: colunasLote,
      rows: lotesPorOperacao[operacao.chave],
    }))
    .concat({
      id: "ambientes",
      label: "Ambientes pendentes",
      columns: COLUNAS_AMBIENTE_EXPORT,
      rows: linhasAmbienteParaExport(filteredLotes),
    });

  const exportVisual = {
    ref: pageRef,
    sections: [
      { id: "resumo", label: "Resumo dos lotes", selector: "[data-pdf='resumo']" },
      SECAO_VISUAL_POR_VISAO[visao],
    ],
  };


  return (
    <Wrapper ref={pageRef}>
      <TopBar>
        <NavegacaoEtapa>
          <Titulo>Industrialização (Máquinas)</Titulo>
        </NavegacaoEtapa>
        <TopBarActions>
          <BuscaInput
            type="search"
            aria-label="Busca geral em industrializacao"
            value={filters.busca}
            onChange={(event) => setFilters((current) => ({ ...current, busca: event.target.value }))}
            placeholder="Lote, cliente, status..."
          />
          <FiltroSoIcone>
          <FilterPanel
            compact
            title="Filtros de industrializacao"
            meta={`${filteredLotes.length} de ${lotes.length} lotes visíveis`}
            open={filtersOpen}
            onToggle={() => setFiltersOpen((current) => !current)}
            onClose={() => setFiltersOpen(false)}
            onReset={() => setFilters({ ...INITIAL_FILTERS })}
            chips={activeFilterChips}
          >
            <FilterGrid>
              <FilterField>
                <FilterLabel>Status</FilterLabel>
                <FiltroSelect
                  value={filters.status}
                  onChange={(event) => setFilters((current) => ({ ...current, status: event.target.value }))}
                >
                  <option value="TODOS">Todos</option>
                  {getUniqueOptions(lotes.map((lote) => lote.status)).map((status) => (
                    <option key={status} value={status}>
                      {status}
                    </option>
                  ))}
                </FiltroSelect>
              </FilterField>
              <FilterField>
                <FilterLabel>Cliente</FilterLabel>
                <FiltroSelect
                  value={filters.cliente}
                  onChange={(event) => setFilters((current) => ({ ...current, cliente: event.target.value }))}
                >
                  {clienteOptions.map((cliente) => (
                    <option key={cliente} value={cliente}>
                      {cliente === "TODOS" ? "Todos" : cliente}
                    </option>
                  ))}
                </FiltroSelect>
              </FilterField>
              <FilterField>
                <FilterLabel>Lote</FilterLabel>
                <BuscaInput
                  value={filters.lote}
                  onChange={(event) => setFilters((current) => ({ ...current, lote: event.target.value }))}
                  placeholder="Filtrar por lote"
                />
              </FilterField>
              <FilterField>
                <FilterLabel>Limite das máquinas</FilterLabel>
                <FilterInlineGroup>
                  <BuscaInput
                    type="date"
                    value={filters.limiteDe}
                    onChange={(event) => setFilters((current) => ({ ...current, limiteDe: event.target.value }))}
                  />
                  <BuscaInput
                    type="date"
                    value={filters.limiteAte}
                    onChange={(event) => setFilters((current) => ({ ...current, limiteAte: event.target.value }))}
                  />
                </FilterInlineGroup>
              </FilterField>
            </FilterGrid>
          </FilterPanel>
          </FiltroSoIcone>
          <SoIcone>
            <BotaoExportacao
              titulo="Industrialização (Máquinas)"
              resumo={exportResumo}
              secoes={exportSecoes}
              visual={exportVisual}
              filtrosAtivos={activeFilterChips}
            />
          </SoIcone>
        </TopBarActions>
      </TopBar>

      <VisaoBar>
        <VisaoTabs role="group" aria-label="Modo de visualização">
          {VISOES.map((item) => (
            <VisaoTab
              key={item.id}
              type="button"
              $active={visao === item.id}
              aria-pressed={visao === item.id}
              onClick={() => setVisao(item.id)}
            >
              {item.label}
            </VisaoTab>
          ))}
        </VisaoTabs>
        <VisaoControles>
          {visao === "tabela" && <SeletorModoTabela modo={modoTabela} onChange={setModoTabela} />}
          {visao === "calendario" && (
            <ToggleCheck title="Mostra cada corte, bordo e furação no dia em que foi feito">
              <input
                type="checkbox"
                checked={realizados}
                onChange={(event) => setRealizadosPref(event.target.checked ? "1" : "0")}
              />
              <span>Mostrar realizados</span>
            </ToggleCheck>
          )}
          {visao === "historico" && <SeletorPeriodo periodo={periodo} onChange={setPeriodo} />}
        </VisaoControles>
      </VisaoBar>

      {visao !== "historico" && (
        <SummaryGrid data-pdf="resumo">
          <SummaryCard>
            <SummaryLabel>Total de Lotes</SummaryLabel>
            <SummaryValue>{resumo.totalLotes}</SummaryValue>
            <SummarySub>lotes com pendência em máquinas</SummarySub>
          </SummaryCard>
          <SummaryCard>
            <SummaryLabel>Ambientes Pendentes</SummaryLabel>
            <SummaryValue>{resumo.totalAmbientesPendentes}</SummaryValue>
            <SummarySub>ambientes ainda em {LISTA_OPERACOES}</SummarySub>
          </SummaryCard>
          <SummaryCard>
            <SummaryLabel>Lotes em Atraso</SummaryLabel>
            <SummaryValue $tone={resumo.lotesEmAtraso > 0 ? "danger" : "default"}>
              {resumo.lotesEmAtraso}
            </SummaryValue>
            <SummarySub>com limite de máquinas vencido</SummarySub>
          </SummaryCard>
          <SummaryCard>
            <SummaryLabel>Valor dos Lotes</SummaryLabel>
            <SummaryValue>
              {precosVisiveis ? formatarMoeda(resumo.valorTotal) : "••••••"}
            </SummaryValue>
            <SummarySub>
              {resumo.totalPendencias} pendências em {LISTA_OPERACOES}
            </SummarySub>
          </SummaryCard>
        </SummaryGrid>
      )}

      <div data-pdf="lotes" data-export-expand>
        {visao === "kanban" && (
          <>
            <SecaoTitulo>Lotes em máquinas</SecaoTitulo>
            <KanbanView lotesPorOperacao={lotesPorOperacao} onSelectLote={setLoteSelecionado} />
          </>
        )}
        {visao === "tabela" && (
          <TabelaView
            lotes={filteredLotes}
            modo={modoTabela}
            precosVisiveis={precosVisiveis}
            onSelectLote={setLoteSelecionado}
          />
        )}
        {visao === "calendario" && (
          <CalendarioView
            lotes={filteredLotes}
            dados={dados}
            filters={filters}
            realizados={realizados}
            escala={escala}
            onEscalaChange={setEscala}
            onSelectLoteId={abrirLotePorId}
          />
        )}
        {visao === "historico" && (
          <HistoricoView
            dados={dados}
            filters={filters}
            periodo={periodo}
            onSelectLoteId={abrirLotePorId}
          />
        )}
      </div>

      <LoteDrawer
        lote={loteSelecionado}
        precosVisiveis={precosVisiveis}
        onClose={() => setLoteSelecionado(null)}
      />
    </Wrapper>
  );
}
